import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { Fragment, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { FadeIn } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, xcss } from '@atlaskit/primitives';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AISources, ErrorBoundary as CommonErrorBoundary, useAnalytics } from '../../common';
import { DotAnimationLoader } from '../../common/ui/ai-answer-loading';
import { AiBrand } from '../../constants';
import { messages } from '../../messages';
import { AIAnswerBody } from '../ai-answer-body';
import { AIAnswerResultBlurSection, AIAnswerResultBlurSectionText, AIAnswerResultSection } from '../ai-answer-body/ai-answer-result/styled';
import { AIAnswerBodyWrapper } from '../ai-answer-body/styled';
import { useAIAnswerContext } from '../ai-answer-context';
import { AIAnswerFooter } from '../ai-answer-footer';
import { AIAnswerLoadingDialog } from '../ai-answer-loading/ai-answer-loading-dialog';
import { AIAnswerTitle } from '../ai-answer-title';
import { onAIAnswerLoadingDialogShown, onAIAnswerSourceClick } from '../analytics';
import { ReadingAidsFollowUps } from '../reading-aids-follow-ups';
import { ReadingAidsContainer, ReadingAidsPopupContainer, ReadingAidsPopupContainerGlow, ReadingAidsStreamingContentWrapper, ReadingAidsWrapper } from '../styled';
var sourcesContainerStyles = xcss({
  paddingTop: '0',
  paddingRight: 'space.300',
  paddingBottom: 'space.150',
  paddingLeft: 'space.300'
});
var MAX_SOURCES = 4;
var MarkdownRenderer = /*#__PURE__*/React.lazy(function () {
  return import( /* webpackChunkName: "react-markdown" */'react-markdown').then(function (_ref) {
    var ReactMarkdown = _ref.default;
    return {
      default: ReactMarkdown
    };
  });
});
var StreamedReadingAidsPopupContent = function StreamedReadingAidsPopupContent() {
  var _useAIAnswerContext = useAIAnswerContext(),
    partialResponse = _useAIAnswerContext.partialResponse;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isResultOverflow = _useState2[0],
    setResultOverflow = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isResultExpanded = _useState4[0],
    setResultExpanded = _useState4[1];
  var wrapperRef = useRef(null);
  var contentRef = useRef(null);
  useEffect(function () {
    if (!wrapperRef.current || !contentRef.current) {
      return;
    }
    var renderer = contentRef.current;
    var mutateTelepointer = function mutateTelepointer(mutations) {
      mutations.forEach(function () {
        var _wrapperRef$current$o, _wrapperRef$current, _contentRef$current$o, _contentRef$current;
        if (((_wrapperRef$current$o = (_wrapperRef$current = wrapperRef.current) === null || _wrapperRef$current === void 0 ? void 0 : _wrapperRef$current.offsetHeight) !== null && _wrapperRef$current$o !== void 0 ? _wrapperRef$current$o : 0) < ((_contentRef$current$o = (_contentRef$current = contentRef.current) === null || _contentRef$current === void 0 ? void 0 : _contentRef$current.offsetHeight) !== null && _contentRef$current$o !== void 0 ? _contentRef$current$o : 0)) {
          setResultOverflow(true);
        }
      });
    };
    var observer = new MutationObserver(mutateTelepointer);
    observer.observe(renderer, {
      characterData: true,
      attributes: false,
      childList: true,
      subtree: true
    });
    return function () {
      return observer.disconnect();
    };
  }, []);
  return /*#__PURE__*/React.createElement(FadeIn, null, function (props) {
    var _partialResponse$part;
    return /*#__PURE__*/React.createElement(ReadingAidsWrapper, _extends({
      onClick: function onClick(e) {
        return e.stopPropagation();
      }
    }, props), /*#__PURE__*/React.createElement(AIAnswerTitle, null), /*#__PURE__*/React.createElement(AIAnswerBodyWrapper, null, /*#__PURE__*/React.createElement(AIAnswerResultSection, {
      id: "ai-answer-streamed-result-section",
      ref: wrapperRef,
      isReadingAids: true,
      isExpanded: isResultExpanded
    }, /*#__PURE__*/React.createElement(ReadingAidsStreamingContentWrapper, {
      ref: contentRef
    }, /*#__PURE__*/React.createElement(Suspense, {
      fallback: /*#__PURE__*/React.createElement(Fragment, null)
    }, /*#__PURE__*/React.createElement(MarkdownRenderer, null, (_partialResponse$part = partialResponse === null || partialResponse === void 0 ? void 0 : partialResponse.partialNlpResult) !== null && _partialResponse$part !== void 0 ? _partialResponse$part : '')))), isResultOverflow && !isResultExpanded && /*#__PURE__*/React.createElement(AIAnswerResultBlurSection, {
      onClick: function onClick() {
        return setResultExpanded(true);
      }
    }, /*#__PURE__*/React.createElement(AIAnswerResultBlurSectionText, null, formatMessage(messages.ai_answer_expand_button_label))), /*#__PURE__*/React.createElement(AIAnswerFooter, null)));
  });
};
var ReadingAidsPopupContent = function ReadingAidsPopupContent() {
  var _extraAttributes$feat;
  var _useAIAnswerContext2 = useAIAnswerContext(),
    brand = _useAIAnswerContext2.brand,
    commonAttributes = _useAIAnswerContext2.commonAttributes,
    loading = _useAIAnswerContext2.loading,
    callbacks = _useAIAnswerContext2.callbacks,
    fireAnalyticsEvent = _useAIAnswerContext2.fireAnalyticsEvent,
    readingAidsOptions = _useAIAnswerContext2.readingAidsOptions,
    partialResponse = _useAIAnswerContext2.partialResponse;
  var enableSmartLinkSources = readingAidsOptions === null || readingAidsOptions === void 0 ? void 0 : readingAidsOptions.enableSmartLinkSources;
  var _useAIEventsInstrumen = useAIEventsInstrumentation(),
    trackAIResultAction = _useAIEventsInstrumen.trackAIResultAction;
  var searchSessionId = commonAttributes.searchSessionId,
    sources = commonAttributes.sources,
    extraAttributes = commonAttributes.extraAttributes;
  var readingAidsStreamingEnabled = !!(extraAttributes !== null && extraAttributes !== void 0 && (_extraAttributes$feat = extraAttributes.featureGates) !== null && _extraAttributes$feat !== void 0 && _extraAttributes$feat.readingAidsStreamingEnabled);
  var sourceClickCallback = useCallback(function (index, hostname) {
    var _callbacks$onAIAnswer;
    fireAnalyticsEvent(onAIAnswerSourceClick(_objectSpread({
      sourceIndex: index,
      hostname: hostname
    }, commonAttributes)));
    trackAIResultAction('sourceLinkClicked');
    callbacks === null || callbacks === void 0 || (_callbacks$onAIAnswer = callbacks.onAIAnswerSourceClick) === null || _callbacks$onAIAnswer === void 0 || _callbacks$onAIAnswer.call(callbacks);
  }, [fireAnalyticsEvent, commonAttributes, callbacks, trackAIResultAction]);
  if (loading) {
    if (!!partialResponse && readingAidsStreamingEnabled) {
      return /*#__PURE__*/React.createElement(StreamedReadingAidsPopupContent, null);
    }
    return /*#__PURE__*/React.createElement(AIAnswerLoadingDialog, null);
  }
  return /*#__PURE__*/React.createElement(FadeIn, null, function (props) {
    return /*#__PURE__*/React.createElement(ReadingAidsWrapper, _extends({
      onClick: function onClick(e) {
        return e.stopPropagation();
      }
    }, props), /*#__PURE__*/React.createElement(AIAnswerTitle, null), /*#__PURE__*/React.createElement(AIAnswerBody, null), /*#__PURE__*/React.createElement(Box, {
      xcss: sourcesContainerStyles
    }, /*#__PURE__*/React.createElement(AISources, {
      sources: sources,
      searchSessionId: searchSessionId,
      onClickHandler: sourceClickCallback,
      limit: MAX_SOURCES,
      verticalLayout: true,
      enableSmartLinkSources: enableSmartLinkSources
    })), brand === AiBrand.ROVO && /*#__PURE__*/React.createElement(CommonErrorBoundary, null, /*#__PURE__*/React.createElement(ReadingAidsFollowUps, null)), /*#__PURE__*/React.createElement(AIAnswerFooter, null));
  });
};
export var DefinitionAnswerCard = function DefinitionAnswerCard() {
  var _useAIAnswerContext3 = useAIAnswerContext(),
    loading = _useAIAnswerContext3.loading,
    commonAttributes = _useAIAnswerContext3.commonAttributes;
  var _useAnalytics = useAnalytics(),
    _useAnalytics2 = _slicedToArray(_useAnalytics, 1),
    fireAnalyticsEvent = _useAnalytics2[0];
  var _useState5 = useState(true),
    _useState6 = _slicedToArray(_useState5, 2),
    continueLoadingAnimation = _useState6[0],
    setContinueLoadingAnimation = _useState6[1];
  var source = commonAttributes.source;
  useEffect(function () {
    if (!loading) {
      var countdownRemainingAnimation = setTimeout(function () {
        setContinueLoadingAnimation(false);
      }, 3000);
      return function () {
        return clearTimeout(countdownRemainingAnimation);
      };
    }
  }, [loading]);
  var onLoadingAnimationShown = useCallback(function () {
    fireAnalyticsEvent(onAIAnswerLoadingDialogShown(commonAttributes, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fireAnalyticsEvent]);
  return loading && fg('kd_definitions_loading_state_confluence') || loading && fg('kd_definitions_loading_state_jira') ? /*#__PURE__*/React.createElement(DotAnimationLoader, {
    onAnimationShown: onLoadingAnimationShown,
    source: source,
    "data-testid": "ai-answer-animated-loader"
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, loading && /*#__PURE__*/React.createElement(ReadingAidsPopupContainerGlow, {
    "data-testid": "ai-answer-legacy-loader"
  }), /*#__PURE__*/React.createElement(ReadingAidsPopupContainer, {
    isLoading: loading,
    continueAnimation: continueLoadingAnimation
  }, /*#__PURE__*/React.createElement(ReadingAidsContainer, null, /*#__PURE__*/React.createElement(ReadingAidsPopupContent, null))));
};